// Per-vehicle digital damage sheet. Owners and staff with the `vehicles`
// permission can:
//
//   * Switch between car views (Front / Back / Left / Right / Top / Interior).
//   * Click any spot on the diagram to drop a numbered marker.
//   * Fill in a preset tag (small scratch, big dent, …) or a custom one,
//     severity, date observed, and freeform notes.
//   * Mark damages as repaired (kept in the list but greyed out) or delete
//     them outright.
//
// The page is intentionally calm and read-only by default: only when the
// user clicks "Add damage" or an empty spot on the diagram do we open the
// editor. This avoids accidental markers.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Check, RotateCcw, X, Upload, ImagePlus, AlertTriangle, ChevronRight, Link2, LinkIcon } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "../lib/api";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";
import {
  DAMAGE_TAGS, tagByCode, SEVERITY_OPTIONS, VIEW_OPTIONS,
  SEVERITY_DOT_CLASS, todayISO, detectVehicleType, defaultViewsForType,
} from "../lib/damages";
import { VehicleDamageMap } from "../components/VehicleDamageMap";

export default function VehicleDamages() {
  const { id } = useParams();
  const nav = useNavigate();

  const [vehicle, setVehicle] = useState(null);
  const [damages, setDamages] = useState([]);
  const [customMaps, setCustomMaps] = useState([]);
  const [view, setView] = useState("front"); // "front"/"back"/... OR "custom:<map_id>"
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showRepaired, setShowRepaired] = useState(false);
  const [uploadingMap, setUploadingMap] = useState(false);
  const [incidents, setIncidents] = useState([]);

  const vehicleType = useMemo(() => detectVehicleType(vehicle?.category), [vehicle]);
  const builtInViews = useMemo(() => defaultViewsForType(vehicleType), [vehicleType]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [v, d, m, inc] = await Promise.all([
        api.get(`/vehicles/${id}`),
        api.get(`/vehicles/${id}/damages`),
        api.get(`/vehicles/${id}/damage-maps`).catch(() => ({ data: { items: [] } })),
        api.get(`/incidents`, { params: { vehicle_id: id } }).catch(() => ({ data: [] })),
      ]);
      setVehicle(v.data.vehicle);
      setDamages(d.data.items || []);
      setCustomMaps(m.data.items || []);
      setIncidents(Array.isArray(inc.data) ? inc.data : (inc.data.items || []));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [id]);

  const currentCustomMap = useMemo(() => {
    if (!view.startsWith("custom:")) return null;
    const mid = view.slice("custom:".length);
    return customMaps.find((m) => m.id === mid) || null;
  }, [view, customMaps]);

  const visibleDamages = useMemo(() => {
    return damages.filter((d) => showRepaired || d.status !== "repaired");
  }, [damages, showRepaired]);

  const activeCount = damages.filter((d) => d.status === "active").length;
  const repairedCount = damages.filter((d) => d.status === "repaired").length;

  const startNew = (seed) => {
    setEditing({
      mode: "new",
      data: {
        view: seed?.view || view,
        x_pct: seed?.x_pct ?? 50,
        y_pct: seed?.y_pct ?? 50,
        tag_code: "small_scratch",
        tag_label: "",
        severity: "small",
        observed_date: todayISO(),
        notes: "",
        status: "active",
      },
    });
  };

  const openExisting = (d) => setEditing({ mode: "edit", data: { ...d } });

  const closeEditor = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const d = editing.data;
    const body = {
      view: d.view,
      x_pct: d.x_pct,
      y_pct: d.y_pct,
      tag_code: d.tag_code,
      tag_label: d.tag_code === "other" ? (d.tag_label || "Other") : (d.tag_label || null),
      severity: d.severity,
      observed_date: d.observed_date,
      notes: d.notes || null,
      status: d.status,
    };
    try {
      if (editing.mode === "new") {
        await api.post(`/vehicles/${id}/damages`, body);
        toast.success("Damage logged");
      } else {
        await api.put(`/vehicles/${id}/damages/${d.id}`, body);
        toast.success("Damage updated");
      }
      closeEditor();
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleRepaired = async (d) => {
    try {
      const next = d.status === "repaired" ? "active" : "repaired";
      await api.put(`/vehicles/${id}/damages/${d.id}`, { status: next });
      toast.success(next === "repaired" ? "Marked as repaired" : "Marked as active");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (d) => {
    if (!window.confirm("Delete this damage entry? This cannot be undone.")) return;
    try {
      await api.delete(`/vehicles/${id}/damages/${d.id}`);
      toast.success("Damage removed");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadCustomMap = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large: max 10MB.");
      return;
    }
    setUploadingMap(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Friendlier default name: prefer photo number, fallback to cleaned stem
      const rawStem = (file.name || "").replace(/\.[^.]+$/, "").trim();
      const cleanedStem = rawStem.length >= 3 ? rawStem.slice(0, 40) : "";
      const fallbackName = `Photo view ${customMaps.length + 1}`;
      formData.append("view_name", cleanedStem || fallbackName);
      formData.append("view_type", "uploaded_image");
      const { data: created } = await api.post(`/vehicles/${id}/damage-maps`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Custom damage map uploaded");
      await loadAll();
      if (created?.id) setView(`custom:${created.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingMap(false);
    }
  };

  const deleteCustomMap = async (mapId) => {
    if (!window.confirm("Delete this custom damage map? Markers placed on it will be hidden.")) return;
    try {
      await api.delete(`/vehicles/${id}/damage-maps/${mapId}`);
      toast.success("Custom map deleted");
      if (view === `custom:${mapId}`) setView("front");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <div className="text-slate-500" data-testid="damages-loading">Loading…</div>;
  if (!vehicle) return <div className="text-slate-500">Vehicle not found.</div>;

  return (
    <div className="space-y-6" data-testid="vehicle-damages-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Damage &amp; Issues</div>
          <h1 className="font-display text-3xl font-bold mt-1">{vehicle.name}</h1>
          <p className="text-sm text-slate-500 font-mono">{vehicle.plate_number || "No plate"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-900" data-testid="damages-active-count">
            {activeCount} active
          </span>
          {repairedCount > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">
              {repairedCount} repaired
            </span>
          )}
          <Link
            to={`/app/incidents/new?vehicle_id=${id}`}
            data-testid="vd-report-issue-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-50 text-sm font-medium"
          >
            <AlertTriangle className="w-4 h-4" /> Report incident
          </Link>
          <button
            onClick={() => startNew()}
            data-testid="damages-add-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg btn-brand text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Add damage
          </button>
        </div>
      </div>

      {/* ----------------------------------------------------------------
            Issues / incidents panel.  Everything that isn't a marker on
            the damage diagram lives here: accidents, breakdowns,
            mechanical issues, customer complaints, missing items,
            late-return problems. They use the existing /api/incidents
            collection: we just filter to this vehicle and show a
            compact list, with a deep link into the full Incident
            Center for the heavier workflow.
           ---------------------------------------------------------------- */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="vd-issues-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-display text-lg font-semibold">Issues &amp; incidents</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Accidents, breakdowns, mechanical issues, customer-reported problems and late-return issues for this vehicle.
            </p>
          </div>
          <Link
            to={`/app/incidents?vehicle_id=${id}`}
            data-testid="vd-issues-view-all"
            className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
          >
            View in Incident Center <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {incidents.length === 0 ? (
          <div className="mt-3 text-sm text-slate-500" data-testid="vd-issues-empty">
            No incidents reported on this vehicle yet. Use “Report incident” above when something happens during a rental.
          </div>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100" data-testid="vd-issues-list">
            {incidents.slice(0, 8).map((i) => {
              const status = i.status || "open";
              const sev = (i.severity || "medium").toLowerCase();
              const statusTone = status === "resolved" || status === "closed"
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : status === "cancelled"
                ? "bg-slate-100 border-slate-200 text-slate-600"
                : "bg-amber-50 border-amber-200 text-amber-800";
              const sevTone = sev === "critical" || sev === "high"
                ? "text-red-700"
                : sev === "low" ? "text-slate-500" : "text-amber-700";
              return (
                <li key={i.id} className="py-3 flex items-center justify-between gap-3" data-testid={`vd-issue-${i.id}`}>
                  <div className="min-w-0">
                    <Link
                      to={`/app/incidents/${i.id}`}
                      className="font-medium text-slate-900 hover:underline truncate block"
                    >
                      {i.title || "Untitled incident"}
                    </Link>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span className="capitalize">{(i.incident_type || "other").replace(/_/g, " ")}</span>
                      <span className={`ml-2 ${sevTone}`}>· {sev}</span>
                      {i.created_at && (
                        <span className="text-slate-400 ml-2">· {new Date(i.created_at).toLocaleDateString()}</span>
                      )}
                      {i.affected_count > 0 && (
                        <span className="ml-2 text-red-700">· {i.affected_count} affected booking{i.affected_count === 1 ? "" : "s"}</span>
                      )}
                    </div>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap capitalize ${statusTone}`}>
                    {status.replace(/_/g, " ")}
                  </span>
                </li>
              );
            })}
            {incidents.length > 8 && (
              <li className="pt-3 text-xs text-slate-500">
                Showing the 8 most recent: open the Incident Center for the full history.
              </li>
            )}
          </ul>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
        {/* View tabs: built-in + custom uploaded views */}
        <div className="flex flex-wrap items-center gap-2" data-testid="damages-view-tabs">
          <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-100 w-fit">
            {builtInViews.map((v) => {
              const count = damages.filter((d) => d.view === v.value && (showRepaired || d.status !== "repaired")).length;
              return (
                <button
                  key={v.value}
                  onClick={() => setView(v.value)}
                  data-testid={`damages-view-${v.value}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                    view === v.value ? "bg-white shadow-sm text-[#1B4332]" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {v.label}{count > 0 && <span className="ml-1 text-[10px] text-slate-500">({count})</span>}
                </button>
              );
            })}
          </div>
          {customMaps.length > 0 && (
            <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-emerald-50 border border-emerald-100 w-fit">
              {customMaps.map((m) => {
                const v = `custom:${m.id}`;
                const count = damages.filter((d) => d.view === v && (showRepaired || d.status !== "repaired")).length;
                return (
                  <button
                    key={m.id}
                    onClick={() => setView(v)}
                    data-testid={`damages-view-custom-${m.id}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition inline-flex items-center gap-1 ${
                      view === v ? "bg-white shadow-sm text-[#1B4332]" : "text-emerald-800 hover:text-emerald-900"
                    }`}
                    title={m.view_name}
                  >
                    <ImagePlus className="w-3 h-3" />
                    {m.view_name || "Custom"}
                    {count > 0 && <span className="ml-0.5 text-[10px] text-slate-500">({count})</span>}
                  </button>
                );
              })}
            </div>
          )}
          <label
            className={`px-3 py-1.5 rounded-lg border border-dashed border-slate-300 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 inline-flex items-center gap-1 cursor-pointer ${uploadingMap ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="damages-upload-custom-map"
          >
            <Upload className="w-3 h-3" /> {uploadingMap ? "Uploading…" : "Upload custom view"}
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              disabled={uploadingMap}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadCustomMap(f); }}
              data-testid="damages-upload-custom-map-input"
            />
          </label>
          {currentCustomMap && (
            <button
              onClick={() => deleteCustomMap(currentCustomMap.id)}
              data-testid={`damages-delete-custom-${currentCustomMap.id}`}
              className="px-2 py-1.5 rounded-lg border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 text-xs inline-flex items-center gap-1"
              title="Delete this custom view"
            >
              <Trash2 className="w-3 h-3" /> Delete view
            </button>
          )}
        </div>

        <p className="text-xs text-slate-500 mt-3">
          {currentCustomMap
            ? `Custom photo view: click anywhere on the image to drop a numbered marker.`
            : `Click anywhere on the ${vehicleType === "motorbike" ? "motorbike" : "car"} sketch to drop a numbered marker, or click an existing marker to edit / mark it repaired.`}
        </p>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-2 sm:p-4">
          <VehicleDamageMap
            vehicleType={vehicleType}
            view={view.startsWith("custom:") ? "front" : view}
            customImage={currentCustomMap}
            damages={visibleDamages}
            selectedId={editing?.data?.id}
            onClickEmpty={startNew}
            onSelect={openExisting}
          />
        </div>

        <div className="mt-3 flex items-center justify-between">
          <Legend />
          <label className="text-xs text-slate-600 inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showRepaired}
              onChange={(e) => setShowRepaired(e.target.checked)}
              data-testid="damages-toggle-repaired"
            />
            Show repaired
          </label>
        </div>
      </div>

      {/* List view */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="damages-list">
        <div className="px-4 sm:px-6 py-3 border-b border-slate-100">
          <h2 className="font-display text-base font-semibold">All damages</h2>
        </div>
        {visibleDamages.length === 0 ? (
          <div className="px-4 sm:px-6 py-10 text-center text-sm text-slate-500" data-testid="damages-empty">
            {damages.length === 0
              ? "No damages logged yet. Add a damage to start the digital damage sheet for this vehicle."
              : "No damages match the current filter."}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visibleDamages.map((d, i) => {
              const tag = tagByCode(d.tag_code);
              return (
                <li key={d.id} className="px-4 sm:px-6 py-3" data-testid={`damages-row-${d.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-bold">{i + 1}</span>
                        <span className="font-medium">
                          {d.tag_code === "other" ? (d.tag_label || tag.label) : tag.label}
                        </span>
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${SEVERITY_DOT_CLASS[d.severity] || "bg-slate-400"}`} title={d.severity} />
                        <span className="text-xs text-slate-500 uppercase tracking-wider">{d.severity}</span>
                        <span className="text-xs text-slate-500">· {viewLabel(d.view, customMaps)}</span>
                        {d.status === "repaired" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 uppercase tracking-wider">Repaired</span>
                        )}
                      </div>
                      {d.notes && <p className="text-sm text-slate-600 mt-1">{d.notes}</p>}
                      <p className="text-xs text-slate-500 mt-1">
                        Observed {d.observed_date || "-"}
                        {d.created_by?.name && <> · by {d.created_by.name}</>}
                      </p>
                      <LinkedIncidentChips
                        damage={d}
                        incidents={incidents}
                        onLink={async (incId) => {
                          try {
                            await api.post(`/vehicles/${id}/damages/${d.id}/link-incident`, { incident_id: incId });
                            toast.success("Damage linked to incident");
                            await loadAll();
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        onUnlink={async (incId) => {
                          try {
                            await api.delete(`/vehicles/${id}/damages/${d.id}/link-incident/${incId}`);
                            toast.success("Unlinked from incident");
                            await loadAll();
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                      />
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openExisting(d)} data-testid={`damages-edit-${d.id}`} className="text-xs px-2.5 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50">Edit</button>
                      <button onClick={() => toggleRepaired(d)} data-testid={`damages-toggle-${d.id}`} className="text-xs px-2.5 py-1 rounded-md border border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-50 inline-flex items-center gap-1">
                        {d.status === "repaired" ? (<><RotateCcw className="w-3 h-3" /> Re-open</>) : (<><Check className="w-3 h-3" /> Repaired</>)}
                      </button>
                      <button onClick={() => remove(d)} data-testid={`damages-delete-${d.id}`} className="text-xs px-2.5 py-1 rounded-md border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 inline-flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="text-xs text-slate-500">
        <Link to={`/app/vehicles/${id}`} className="text-[#1B4332] hover:underline">← Back to vehicle</Link>
      </div>

      {editing && (
        <DamageEditor
          editing={editing}
          setEditing={setEditing}
          onCancel={closeEditor}
          onSave={save}
        />
      )}
    </div>
  );
}

function viewLabel(viewKey, customMaps) {
  if (typeof viewKey === "string" && viewKey.startsWith("custom:")) {
    const mid = viewKey.slice("custom:".length);
    const m = (customMaps || []).find((x) => x.id === mid);
    return m ? `Custom · ${m.view_name || "Photo"}` : "Custom view";
  }
  return VIEW_OPTIONS.find((v) => v.value === viewKey)?.label || viewKey || "-";
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
      <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-500">Severity</span>
      {SEVERITY_OPTIONS.map((s) => (
        <span key={s.value} className="inline-flex items-center gap-1.5">
          <span className={`w-3 h-3 rounded-full ${SEVERITY_DOT_CLASS[s.value]}`} /> {s.label}
        </span>
      ))}
    </div>
  );
}

function DamageEditor({ editing, setEditing, onCancel, onSave }) {
  const { mode, data } = editing;
  const update = (k, v) => setEditing({ ...editing, data: { ...data, [k]: v } });
  const isCustom = data.tag_code === "other";
  const { panelRef } = useAccessibleOverlay(true, onCancel);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto" data-testid="damage-editor">
      <div className="min-h-full flex items-center justify-center p-4">
        <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="damage-editor-title"
             className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full my-4">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 id="damage-editor-title" className="font-display font-semibold">{mode === "new" ? "Add damage" : "Edit damage"}</h3>
          <button type="button" onClick={onCancel} aria-label="Close damage editor" data-testid="damage-editor-close" className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 grid sm:grid-cols-2 gap-4">
          <label className="sm:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Damage type</span>
            <select
              data-testid="damage-tag-select"
              value={data.tag_code}
              onChange={(e) => update("tag_code", e.target.value)}
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none"
            >
              {DAMAGE_TAGS.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </label>

          {isCustom && (
            <label className="sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Custom label</span>
              <input
                data-testid="damage-custom-label"
                value={data.tag_label || ""}
                onChange={(e) => update("tag_label", e.target.value)}
                placeholder="e.g. broken side mirror"
                className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none"
              />
            </label>
          )}

          <label>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Severity</span>
            <select
              data-testid="damage-severity-select"
              value={data.severity}
              onChange={(e) => update("severity", e.target.value)}
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none"
            >
              {SEVERITY_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>

          <label>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Date observed</span>
            <input
              data-testid="damage-date-input"
              type="date"
              value={data.observed_date || ""}
              onChange={(e) => update("observed_date", e.target.value)}
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none"
            />
          </label>

          <label>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">View</span>
            <select
              data-testid="damage-view-select"
              value={data.view}
              onChange={(e) => update("view", e.target.value)}
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none"
            >
              {VIEW_OPTIONS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          </label>

          <label>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Status</span>
            <select
              data-testid="damage-status-select"
              value={data.status}
              onChange={(e) => update("status", e.target.value)}
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none"
            >
              <option value="active">Active</option>
              <option value="repaired">Repaired</option>
            </select>
          </label>

          <label className="sm:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Notes (how / where / when)
            </span>
            <textarea
              data-testid="damage-notes-input"
              rows={3}
              value={data.notes || ""}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Optional. E.g. happened when client parked at SM Cebu, customer admitted at return."
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none"
            />
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>Marker position: {Math.round(data.x_pct)}%, {Math.round(data.y_pct)}%</span>
          <div className="flex gap-2">
            <button onClick={onCancel} className="px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Cancel</button>
            <button onClick={onSave} data-testid="damage-editor-save" className="px-4 py-2 rounded-lg btn-brand text-sm font-medium">Save</button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}


// Chips rendering linked incidents on a damage row + a small picker to link more.
function LinkedIncidentChips({ damage, incidents, onLink, onUnlink }) {
  const linkedIds = damage.linked_incident_ids || [];
  const [picking, setPicking] = useState(false);
  const linkedMap = new Map(incidents.map((i) => [i.id, i]));
  const linkable = incidents.filter(
    (i) => !linkedIds.includes(i.id) && i.status !== "resolved" && i.status !== "cancelled"
  );

  if (linkedIds.length === 0 && !picking) {
    return (
      <div className="mt-2" data-testid={`damage-link-${damage.id}`}>
        {linkable.length > 0 ? (
          <button
            type="button"
            onClick={() => setPicking(true)}
            className="text-[11px] inline-flex items-center gap-1 text-slate-500 hover:text-[#1B4332]"
            data-testid={`damage-link-open-${damage.id}`}
          >
            <Link2 className="w-3 h-3" /> Link to incident
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`damage-link-${damage.id}`}>
      {linkedIds.map((iid) => {
        const inc = linkedMap.get(iid);
        return (
          <span
            key={iid}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-800"
            data-testid={`damage-${damage.id}-incident-${iid}`}
          >
            <LinkIcon className="w-3 h-3" />
            <Link to={`/app/incidents/${iid}`} className="hover:underline">
              {inc ? (inc.title || "Incident").slice(0, 40) : `Incident ${iid.slice(0, 8)}`}
            </Link>
            <button
              type="button"
              onClick={() => onUnlink(iid)}
              className="ml-0.5 text-rose-600 hover:text-rose-800"
              title="Unlink"
              data-testid={`damage-${damage.id}-unlink-${iid}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      })}
      {picking ? (
        <select
          autoFocus
          className="text-[11px] px-1.5 py-0.5 rounded border border-slate-300 bg-white"
          data-testid={`damage-link-select-${damage.id}`}
          onChange={async (e) => {
            const val = e.target.value;
            if (val) { await onLink(val); }
            setPicking(false);
          }}
          onBlur={() => setPicking(false)}
          defaultValue=""
        >
          <option value="">Pick incident…</option>
          {linkable.map((i) => (
            <option key={i.id} value={i.id}>
              {(i.title || "Incident").slice(0, 50)} · {i.severity || ""}
            </option>
          ))}
        </select>
      ) : (
        linkable.length > 0 && (
          <button
            type="button"
            onClick={() => setPicking(true)}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:text-[#1B4332] hover:border-slate-400"
            data-testid={`damage-link-add-${damage.id}`}
          >
            <Plus className="w-3 h-3" /> Link incident
          </button>
        )
      )}
    </div>
  );
}
